import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Monitor, Moon, Sun, Type, Palette, Save } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const STORAGE_KEY = 'nn_appearance_prefs';

const DEFAULTS = { theme: 'light', accentColor: '#E10D2C', fontSize: 'medium', compactMode: false, animations: true };

function loadPrefs() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    // Also sync theme from AdminLayout's 'theme' key
    const adminTheme = localStorage.getItem('theme');
    if (adminTheme && !raw.theme) raw.theme = adminTheme;
    return { ...DEFAULTS, ...raw };
  } catch { return DEFAULTS; }
}

/** Apply all appearance settings to the DOM immediately */
function applyToDOM(prefs) {
  const root = document.documentElement;

  // ── Theme (syncs with AdminLayout's useAdminTheme) ──
  let isDark = prefs.theme === 'dark';
  if (prefs.theme === 'system') {
    isDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  }
  if (isDark) {
    root.classList.add('dark');
    localStorage.setItem('theme', 'dark');
  } else {
    root.classList.remove('dark');
    localStorage.setItem('theme', 'light');
  }

  // ── Accent color (CSS custom property) ──
  root.style.setProperty('--nn-accent', prefs.accentColor);
  root.style.setProperty('--nn-accent-rgb', hexToRgb(prefs.accentColor));

  // ── Font size ──
  root.style.fontSize = prefs.fontSize === 'small' ? '14px' : prefs.fontSize === 'large' ? '18px' : '16px';

  // ── Compact mode ──
  root.classList.toggle('compact', !!prefs.compactMode);

  // ── Animations ──
  root.classList.toggle('no-animations', !prefs.animations);
}

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

const THEME_OPTIONS = [
  { value: 'light', label: 'Light', icon: Sun, preview: 'bg-white border-[#E7E0D6]' },
  { value: 'dark', label: 'Dark', icon: Moon, preview: 'bg-[#121316] border-[#2A2D33]' },
  { value: 'system', label: 'System', icon: Monitor, preview: 'bg-gradient-to-r from-white to-[#121316] border-[#E7E0D6]' },
];

const ACCENT_COLORS = [
  { value: '#E10D2C', label: 'Signal Red' },
  { value: '#1E5A8A', label: 'Blue' },
  { value: '#1F7A5A', label: 'Green' },
  { value: '#B3261E', label: 'Crimson' },
  { value: '#7C3AED', label: 'Purple' },
  { value: '#0891B2', label: 'Teal' },
];

export default function AppearanceSetting() {
  const [prefs, setPrefs] = useState(loadPrefs);
  const { theme, accentColor, fontSize, compactMode, animations } = prefs;

  const set = (key, value) => {
    setPrefs(prev => {
      const next = { ...prev, [key]: value };
      // Apply theme and accent instantly for live preview
      if (key === 'theme' || key === 'accentColor') applyToDOM(next);
      return next;
    });
  };

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    applyToDOM(prefs);
    // Dispatch storage event so AdminLayout's useAdminTheme picks up the change
    window.dispatchEvent(new Event('storage'));
    toast.success('Appearance settings saved');
  };

  // Apply on mount
  useEffect(() => {
    applyToDOM(loadPrefs());
  }, []);

  return (
    <div className="space-y-6">
      <Card className="bg-white dark:bg-[#1A1D23] border-[#E7E0D6] dark:border-white/[0.06]">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Palette className="w-4 h-4" style={{ color: accentColor }} /> Theme
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            {THEME_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => set('theme', opt.value)}
                className={cn(
                  'flex flex-col items-center gap-2 rounded-xl border-2 p-4 transition-all',
                  theme === opt.value
                    ? 'ring-2 ring-offset-1'
                    : 'border-[#E7E0D6] dark:border-white/10 hover:border-[#E10D2C]/50'
                )}
                style={theme === opt.value ? { borderColor: accentColor, '--tw-ring-color': accentColor + '33' } : {}}
              >
                <div className={cn('w-full h-16 rounded-lg border', opt.preview)} />
                <div className="flex items-center gap-1.5 text-xs font-medium">
                  <opt.icon className="w-3.5 h-3.5" />
                  {opt.label}
                </div>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white dark:bg-[#1A1D23] border-[#E7E0D6] dark:border-white/[0.06]">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Accent Color</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {ACCENT_COLORS.map(color => (
              <button
                key={color.value}
                onClick={() => set('accentColor', color.value)}
                className="flex flex-col items-center gap-1.5 group"
              >
                <div
                  className={cn(
                    'w-10 h-10 rounded-full transition-all ring-offset-2',
                    accentColor === color.value ? 'ring-2 scale-110' : 'hover:scale-105'
                  )}
                  style={{
                    backgroundColor: color.value,
                    ...(accentColor === color.value ? { '--tw-ring-color': color.value } : {}),
                  }}
                />
                <span className={cn('text-[10px]', accentColor === color.value ? 'font-semibold' : 'text-[#5B616E]')}
                  style={accentColor === color.value ? { color: color.value } : {}}
                >{color.label}</span>
              </button>
            ))}
          </div>
          {/* Live preview swatch */}
          <div className="mt-4 flex items-center gap-3 p-3 rounded-xl border border-[#E7E0D6] dark:border-white/[0.06]">
            <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: accentColor }} />
            <div>
              <p className="text-xs font-semibold" style={{ color: accentColor }}>Active accent: {ACCENT_COLORS.find(c => c.value === accentColor)?.label || accentColor}</p>
              <p className="text-[10px] text-[#5B616E]">{accentColor}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white dark:bg-[#1A1D23] border-[#E7E0D6] dark:border-white/[0.06]">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Type className="w-4 h-4" /> Typography & Layout
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Font Size</Label>
              <p className="text-xs text-[#5B616E]">Adjust the base font size for the admin panel</p>
            </div>
            <Select value={fontSize} onValueChange={v => set('fontSize', v)}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="small">Small</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="large">Large</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="h-px bg-[#E7E0D6] dark:bg-white/[0.06]" />
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Compact Mode</Label>
              <p className="text-xs text-[#5B616E]">Reduce spacing and padding throughout the UI</p>
            </div>
            <Switch checked={compactMode} onCheckedChange={v => set('compactMode', v)} />
          </div>
          <div className="h-px bg-[#E7E0D6] dark:bg-white/[0.06]" />
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Animations</Label>
              <p className="text-xs text-[#5B616E]">Enable smooth transitions and micro-animations</p>
            </div>
            <Switch checked={animations} onCheckedChange={v => set('animations', v)} />
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={handleSave} style={{ backgroundColor: accentColor }} className="text-white hover:opacity-90">
              <Save className="w-4 h-4 mr-2" /> Save Appearance
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
